import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X, Check } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem("cookie-consent");
    if (!consent) {
      // Show banner after a short delay
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptAll = () => {
    localStorage.setItem("cookie-consent", JSON.stringify({
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  const acceptNecessary = () => {
    localStorage.setItem("cookie-consent", JSON.stringify({
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 200 }}
        className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6"
        data-testid="cookie-consent-banner"
      >
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-[#E6E2D6] overflow-hidden">
          {/* Main Banner */}
          <div className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#F9F7F2] rounded-full flex items-center justify-center flex-shrink-0">
                <Cookie size={24} className="text-[#9A8C73]" weight="fill" />
              </div>
              
              <div className="flex-grow">
                <h3 
                  className="text-lg font-medium mb-2"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Wir respektieren deine Privatsphäre
                </h3>
                <p className="text-sm text-[#2C2C2C]/70 mb-4">
                  Wir verwenden Cookies, um dir die bestmögliche Erfahrung auf unserer Website zu bieten. 
                  Einige Cookies sind notwendig für die Funktion der Website, während andere uns helfen, 
                  die Website zu verbessern und personalisierte Inhalte anzuzeigen.
                </p>

                {/* Cookie Details (expandable) */}
                <AnimatePresence>
                  {showDetails && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="mb-4 overflow-hidden"
                    >
                      <div className="bg-[#F9F7F2] rounded-lg p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm">Notwendige Cookies</p>
                            <p className="text-xs text-[#2C2C2C]/60">Erforderlich für die Grundfunktionen</p>
                          </div>
                          <span className="text-xs bg-[#9A8C73] text-white px-2 py-1 rounded">Immer aktiv</span>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm">Analyse Cookies</p>
                            <p className="text-xs text-[#2C2C2C]/60">Helfen uns, die Website zu verbessern</p>
                          </div>
                          <span className="text-xs text-[#2C2C2C]/60">Optional</span>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm">Marketing Cookies</p>
                            <p className="text-xs text-[#2C2C2C]/60">Für personalisierte Werbung</p>
                          </div>
                          <span className="text-xs text-[#2C2C2C]/60">Optional</span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Links */}
                <div className="flex flex-wrap gap-4 text-xs text-[#9A8C73] mb-4">
                  <button 
                    onClick={() => setShowDetails(!showDetails)}
                    className="hover:underline"
                  >
                    {showDetails ? "Details ausblenden" : "Mehr erfahren"}
                  </button>
                  <Link to="/datenschutz" className="hover:underline">Datenschutz</Link>
                  <Link to="/agb" className="hover:underline">AGB</Link>
                </div>

                {/* Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <motion.button
                    onClick={acceptAll}
                    data-testid="cookie-accept-all"
                    className="flex items-center justify-center gap-2 bg-[#2C2C2C] text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-[#D4AF37] transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Check size={18} weight="bold" />
                    Alle akzeptieren
                  </motion.button>
                  
                  <motion.button
                    onClick={acceptNecessary}
                    data-testid="cookie-accept-necessary"
                    className="flex items-center justify-center gap-2 border border-[#2C2C2C] text-[#2C2C2C] px-6 py-3 rounded-full text-sm font-medium hover:bg-[#F9F7F2] transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Nur notwendige
                  </motion.button>
                </div>
              </div>

              {/* Close button */}
              <button
                onClick={acceptNecessary}
                className="text-[#2C2C2C]/40 hover:text-[#2C2C2C] transition-colors"
                aria-label="Schließen"
              >
                <X size={24} />
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsent;
